/**
 * RFC 4180 CSV reading: BOM stripping, delimiter auto-detection, quoted fields
 * with embedded delimiters/newlines/escaped quotes, and strict UTF-8 decoding.
 *
 * `stripBom` and `detectDelimiter` were moved here from
 * apps/api/src/services/banking/csv_parser.ts (which now re-exports them) so the
 * one quote-aware delimiter detector is shared by the bank-statement importer
 * and this package. The banking-specific rest (Xero asterisk headers, date
 * detection, amount parsing) stays in apps/api.
 */

import { SpreadsheetError, type RawSheet } from '../types.js';

const BOM = '﻿';

/** Strip a leading UTF-8 BOM if present. */
export function stripBom(text: string): string {
  return text.startsWith(BOM) ? text.slice(1) : text;
}

/**
 * Detect the most likely delimiter by counting occurrences in the first
 * non-empty line, ignoring quoted regions. Ties break comma → semicolon → tab.
 */
export function detectDelimiter(text: string): ',' | ';' | '\t' {
  const firstLine =
    stripBom(text)
      .split(/\r\n|\r|\n/)
      .find((l) => l.trim() !== '') ?? '';
  let inQuotes = false;
  let commas = 0;
  let semis = 0;
  let tabs = 0;
  for (let i = 0; i < firstLine.length; i++) {
    const ch = firstLine[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === ',') commas++;
    else if (ch === ';') semis++;
    else if (ch === '\t') tabs++;
  }
  if (commas >= semis && commas >= tabs && commas > 0) return ',';
  if (semis >= tabs && semis > 0) return ';';
  if (tabs > 0) return '\t';
  return ','; // safe default for single-column files
}

/**
 * Decode raw CSV bytes as strict UTF-8 and strip a BOM. Throws
 * `SpreadsheetError('INVALID_ENCODING')` on any invalid byte sequence (e.g. a
 * Windows-1252 export with a 0x92 smart quote) — we never silently guess a
 * legacy codepage, which would corrupt names and IDs.
 */
export function decodeCsvBytes(bytes: Uint8Array): string {
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new SpreadsheetError(
      'INVALID_ENCODING',
      'This file is not valid UTF-8. Open it in a spreadsheet app and re-save as "CSV UTF-8", then upload again.',
    );
  }
  return stripBom(text);
}

/** Tokenise CSV text into a 2D string array, handling quoted fields. */
function parseRows(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      current.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      current.push(field);
      rows.push(current);
      current = [];
      field = '';
    } else {
      field += ch;
    }
  }
  if (field !== '' || current.length > 0) {
    current.push(field);
    rows.push(current);
  }
  return rows;
}

export interface ReadCsvOptions {
  /** Override delimiter auto-detection. */
  delimiter?: ',' | ';' | '\t';
}

/**
 * Parse decoded CSV text into a dense grid. Fully-empty rows are dropped. The
 * grid is NOT padded to a rectangle beyond what the rows themselves contain —
 * callers that need header alignment use {@link readCsvToSheet}.
 */
export function readCsvGrid(
  text: string,
  options: ReadCsvOptions = {},
): { grid: string[][]; delimiter: string } {
  const stripped = stripBom(text);
  const delimiter = options.delimiter ?? detectDelimiter(stripped);
  const allRows = parseRows(stripped, delimiter);
  const grid = allRows.filter((row) => row.some((cell) => cell.trim() !== ''));
  return { grid, delimiter };
}

/**
 * Parse decoded CSV text into a {@link RawSheet}. The grid is right-padded to
 * the widest row so every row has a cell under every header. CSV carries no
 * cell-type information, so `numericCells` is always empty.
 */
export function readCsvToSheet(
  name: string,
  text: string,
  options: ReadCsvOptions = {},
): RawSheet {
  const { grid } = readCsvGrid(text, options);
  const width = grid.reduce((max, row) => Math.max(max, row.length), 0);
  const padded = grid.map((row) => {
    if (row.length === width) return row;
    const copy = row.slice();
    while (copy.length < width) copy.push('');
    return copy;
  });
  return { name, grid: padded, numericCells: new Set<string>() };
}

/** Whether a value is in scientific/exponential notation (e.g. `2.00E+11`). */
export function isScientificNotation(value: string): boolean {
  return /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(value.trim());
}
